import React, { useState, useEffect } from 'react';
import SideNavigation, {
  SideNavigationProps,
} from '@amzn/awsui-components-console/side-navigation';
import { useLocation } from 'react-router';
import { getSideNavActiveHref } from './utils';
import { useAuthState } from '../../authentication/context';
import {
  GetUserGroupsCommand,
  GetUserGroupsCommandOutput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { callApi } from '../../client';
import { NoTokenError } from '../../errors/NoTokenError';

import Item = SideNavigationProps.Item;

const SideNav: React.FC = () => {
  const [activeHref, setActiveHref] = useState<string>(window.location.hash);
  const [permissions, setPermissions] = useState<string[]>([]);
  const location = useLocation();
  const { isAuthenticated, token } = useAuthState();

  useEffect(() => {
    setActiveHref(getSideNavActiveHref(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated && token) {
      void getUserGroups();
    }
  }, [isAuthenticated, token]);

  const getUserGroups = async () => {
    if (!token) throw new NoTokenError();

    try {
      const command = new GetUserGroupsCommand({});
      const response: GetUserGroupsCommandOutput = await callApi(
        command,
        token
      );

      if (response.$metadata.httpStatusCode === 200) {
        setPermissions(response.permissions || []);
      } else {
        console.error(
          'Failed to fetch user groups. Status code:',
          response.$metadata.httpStatusCode
        );
      }
    } catch (err: any) {
      console.error('Error fetching user groups:', err);
    }
  };

  const getNavigationItems = (): Item[] => {
    const fusionItems: Item[] = [
      {
        type: 'link',
        text: 'Publish content',
        href: 'https://fusion.docs.aws.dev/publish',
      },
      {
        type: 'link',
        text: 'Track publishing',
        href: 'https://fusion.docs.aws.dev/publish-tracking',
      },
    ];
    const launchCoordinatorItems: Item[] = [
      { type: 'link', text: 'Overview', href: '#/' }, // https://integ.conference.virtualsmiley.docs.aws.a2z.com/#/
      { type: 'link', text: 'Launch list', href: '#/conferences' }, // https://integ.conference.virtualsmiley.docs.aws.a2z.com/#/conferences
      { type: 'link', text: 'Groups', href: '#/groups' }, // https://integ.conference.virtualsmiley.docs.aws.a2z.com/#/groups
    ];

    if (permissions.includes('active-packages-access')) {
      launchCoordinatorItems.push({
        type: 'link',
        text: 'Active packages',
        href: '#/active-packages',
      });
    }

    if (permissions.includes('venue-access')) {
      launchCoordinatorItems.push({
        type: 'link',
        text: 'Venue build to version set',
        href: '#/venuevsbuild',
      });
    }

    return [
      {
        type: 'link',
        text: 'Home',
        href: 'https://fusion.docs.aws.dev/home',
      },
      {
        type: 'section',
        text: 'Publishing',
        items: fusionItems,
      },
      {
        type: 'section',
        text: 'Conference Publishing (Launch Coordinator)',
        items: launchCoordinatorItems,
      },
      {
        type: 'divider',
      },
      {
        type: 'link',
        text: 'User guide',
        href: 'https://fusion.docs.aws.dev/user-guide',
      },
      {
        type: 'link',
        text: 'Contact us',
        href: 'https://fusion.docs.aws.dev/home#contact-us',
      },
    ] as Item[];
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{ href: 'https://fusion.docs.aws.dev', text: 'Fusion' }}
      onFollow={(event) => {
        setActiveHref(event.detail.href);
      }}
      items={getNavigationItems()}
    />
  );
};

export default SideNav;
